import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiGlobe, FiUsers, FiMessageSquare, FiMail, FiPhone, FiFileText, FiCalendar, FiBarChart, FiBook, FiStar, FiPlay, FiFolder, FiShoppingCart, FiVideo, FiTv } = FiIcons;

const FeaturesSection = () => {
  const features = [
    {
      icon: FiTarget,
      title: "Unlimited Sales Funnels",
      description: "Landing pages for product demos, service consultations, factory visits, lead magnets, quote requests & pricing pages",
      examples: ["Product demonstrations", "Service consultations", "Factory visit bookings", "Lead magnets", "Quote request funnels"]
    },
    {
      icon: FiGlobe,
      title: "Website Builder",
      description: "No agency needed. Create professional brand websites for manufacturing units, service companies, freelancers",
      examples: ["Manufacturing websites", "Service company sites", "Freelancer portfolios", "Professional branding"]
    },
    {
      icon: FiUsers,
      title: "CRM & Pipeline Management",
      description: "Track every lead from enquiry → follow-up → sale → renewal with complete visibility",
      examples: ["Lead tracking", "Sales pipeline", "Customer journey", "Renewal management"]
    },
    {
      icon: FiMessageSquare,
      title: "WhatsApp Automation",
      description: "Instant replies, auto follow-ups, payment reminders, appointment confirmations & customer nurturing",
      examples: ["Instant auto-replies", "Payment reminders", "Appointment confirmations", "Lead nurturing"]
    },
    {
      icon: FiMail,
      title: "Email & SMS Marketing",
      description: "Send newsletters, promotions, offers, order updates automatically to your customer base",
      examples: ["Newsletter campaigns", "Promotional offers", "Order updates", "Customer communications"]
    },
    {
      icon: FiPhone,
      title: "Call Tracking",
      description: "Know exactly which ad or funnel generates phone calls with detailed analytics",
      examples: ["Call source tracking", "Ad performance", "Funnel analytics", "ROI measurement"]
    },
    {
      icon: FiFileText,
      title: "Surveys & Forms",
      description: "Collect requirements, get client briefs, collect enquiries with smart form builders",
      examples: ["Requirement collection", "Client brief forms", "Enquiry capture", "Feedback surveys"]
    },
    {
      icon: FiCalendar,
      title: "Booking & Appointments",
      description: "Automate client meetings, factory visits, service appointments, coaching sessions & demo calls",
      examples: ["Client meetings", "Factory visits", "Service appointments", "Demo bookings"]
    },
    {
      icon: FiBarChart,
      title: "Tracking & Analytics",
      description: "Deep insights into funnel performance, sales metrics, and business growth analytics",
      examples: ["Funnel performance", "Sales metrics", "Growth analytics", "ROI tracking"]
    },
    {
      icon: FiBook,
      title: "Courses & Products",
      description: "Sell training programs, onboarding courses, employee training & skill development modules",
      examples: ["Training programs", "Onboarding courses", "Employee training", "Skill development"]
    },
    {
      icon: FiStar,
      title: "Reputation Management",
      description: "Collect Google reviews automatically and manage your online reputation effectively",
      examples: ["Auto review collection", "Reputation monitoring", "Review management", "Rating improvement"]
    },
    {
      icon: FiUsers,
      title: "Community + Courses",
      description: "Create premium customer communities for loyalty & repeat business engagement",
      examples: ["Customer communities", "Loyalty programs", "Member engagement", "Repeat business"]
    },
    {
      icon: FiPlay,
      title: "Unlimited Video Hosting",
      description: "Host product demos, training, workshops – no YouTube or Vimeo needed",
      examples: ["Product demos", "Training videos", "Workshops", "Tutorial content"]
    },
    {
      icon: FiFolder,
      title: "Document Hosting",
      description: "Store brochures, catalogues, proposals, SOPs, quotations securely",
      examples: ["Brochures", "Product catalogues", "Proposals", "SOPs & quotations"]
    },
    {
      icon: FiShoppingCart,
      title: "Ecommerce/Merchandise",
      description: "Sell physical products, merchandise, digital products with integrated payment gateways",
      examples: ["Physical products", "Digital merchandise", "Online store", "Payment processing"]
    },
    {
      icon: FiVideo,
      title: "Automated Webinars",
      description: "Auto sales webinars and evergreen lead generation campaigns",
      examples: ["Sales webinars", "Lead generation", "Evergreen content", "Auto presentations"]
    },
    {
      icon: FiTv,
      title: "Live Webinars (1000 attendees)",
      description: "Host sales events, dealer onboarding, training sessions with large audiences",
      examples: ["Sales events", "Dealer onboarding", "Training sessions", "Large presentations"]
    }
  ];

  return (
    <section className="py-20 bg-gray-50 relative overflow-hidden">
      <div className="container mx-auto px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Powerful Features Built for 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> MSME Success</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Every feature designed with real MSME use-cases in mind. No generic solutions - just what works for Indian businesses.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 premium-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <div className="flex items-center mb-6">
                <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center mr-4">
                  <SafeIcon icon={feature.icon} className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">{feature.title}</h3>
              </div>

              <p className="text-gray-600 mb-6 leading-relaxed">{feature.description}</p>

              <div className="space-y-2">
                <h4 className="font-semibold text-gray-800 text-sm uppercase tracking-wide">Use Cases:</h4>
                <ul className="space-y-1">
                  {feature.examples.map((example, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-3"></div>
                      {example}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Ready to Transform Your MSME?</h3>
            <p className="text-lg mb-6 opacity-90">Get a personalized demo showing how these features work for your specific business</p>
            <a 
              href="https://funnelsdone.com/cash" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-bold text-lg rounded-full hover:bg-gray-100 transition-all duration-300"
            >
              👉 Book Your Free Strategy Call
              <SafeIcon icon={FiTarget} className="ml-2 w-5 h-5" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesSection;